import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { HorarioResponse, DiaSemana, DIA_LABEL, DIA_COLOR, DIAS_SEMANA } from '../types/horario.types';
import { horarioService } from '../services/horarioService';
import { catalogoService } from '../services/usuarioService';
import { AñoEscolar, Grado, Seccion } from '../types/usuario.types';
import HorarioFormPage from './HorarioFormPage';
import { getConfig } from '../utils/appConfig';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// CSS de impresión
const PRINT_STYLES = `
@media print {
  @page { size: A4 landscape; margin: 12mm 14mm; }
  body * { visibility: hidden; }
  #horarios-print-area, #horarios-print-area * { visibility: visible; }
  #horarios-print-area { position: absolute; top: 0; left: 0; width: 100%; }

  .horarios-print-section { page-break-after: always; margin-bottom: 0 !important; }
  .horarios-print-section:last-child { page-break-after: auto; }

  .horarios-thead { background-color: #334155 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  .horarios-thead th { color: #ffffff !important; }
  .horarios-tbody tr:nth-child(even) td { background-color: #f8fafc !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

  .horarios-cell-clase { background-color: #eff6ff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  .horarios-cell-recreo { background-color: #f0fdf4 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

  .horarios-section-header { background-color: #f8fafc !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

  .horarios-nivel-badge { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
`;

// ============================================================
// HorariosPage – Lista y vista semanal de horarios
// ============================================================

type Vista = 'list' | 'new' | 'edit';

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS   = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_SEARCH = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_PRINT  = 'M5 4v3H4a3 3 0 0 0-3 3v4a1 1 0 0 0 1 1h2v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2h2a1 1 0 0 0 1-1v-4a3 3 0 0 0-3-3h-1V4a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1Zm9 0H6v3h8V4Zm-8 9h6v3H6v-3Zm8.5-5a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5Z';
const D_EDIT   = 'M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65ZM3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z';
const D_DEL    = 'M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z';

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const BTN_COLOR: Record<string, string> = {
  blue: 'hover:text-blue-600 hover:bg-blue-50',
  red:  'hover:text-red-500  hover:bg-red-50',
};
const ActionBtn: React.FC<{ title: string; color: string; onClick: () => void; children: React.ReactNode }> =
  ({ title, color, onClick, children }) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded-lg text-slate-400 transition-colors ${BTN_COLOR[color]}`}>
      {children}
    </button>
  );

// Formatea HH:mm:ss → HH:mm
const fmt = (t: string) => t?.substring(0, 5) ?? t;

// ── Componente principal ──────────────────────────────────────
const HorariosPage: React.FC = () => {
  const [vista, setVista]   = useState<Vista>('list');
  const [editId, setEditId] = useState<number | null>(null);

  const [horarios, setHorarios]     = useState<HorarioResponse[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);

  // Catálogos para los filtros
  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);
  const [todosGrados, setTodosGrados]     = useState<Grado[]>([]);
  const [todasSecciones, setTodasSecciones] = useState<Seccion[]>([]);

  // Filtros con IDs
  const [search, setSearch]   = useState('');
  const [anioId, setAnioId]   = useState<number | ''>('');
  const [nivel, setNivel]             = useState<string>('');
  const [gradoId, setGradoId]         = useState<number | ''>('');
  const [seccionId, setSeccionId]     = useState<number | ''>('');

  const [deleteId, setDeleteId]       = useState<number | null>(null);
  const [deleting, setDeleting]       = useState(false);

  // Niveles únicos y grados filtrados por nivel
  const niveles      = useMemo(() => Array.from(new Set(todosGrados.map(g => g.nivel))).sort(), [todosGrados]);
  const gradosFiltrados = useMemo(
    () => todosGrados.filter(g => !nivel || g.nivel === nivel),
    [todosGrados, nivel]
  );

  // Elige el endpoint más específico según los filtros activos
  const loadHorarios = useCallback(async (
    _anioId:    number | '' = anioId,
    _nivel:     string      = nivel,
    _gradoId:   number | '' = gradoId,
    _seccionId: number | '' = seccionId,
  ) => {
    setLoading(true); setError(null);
    try {
      let data: HorarioResponse[];
      if (_anioId && _nivel && _gradoId && _seccionId) {
        data = await horarioService.listarFiltrado(_anioId as number, _nivel, _gradoId as number, _seccionId as number);
      } else if (_anioId && _nivel && _gradoId) {
        data = await horarioService.listarPorAnioNivelGrado(_anioId as number, _nivel, _gradoId as number);
      } else if (_anioId && _nivel) {
        data = await horarioService.listarPorAnioNivel(_anioId as number, _nivel);
      } else if (_anioId) {
        data = await horarioService.listarPorAnio(_anioId as number);
      } else {
        data = await horarioService.listar();
      }
      setHorarios(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar horarios');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anioId, nivel, gradoId, seccionId]);

  // Indica si el usuario ya ejecutó al menos una búsqueda
  const [buscado, setBuscado] = useState(false);

  // Carga inicial: solo catálogos, sin cargar horarios
  useEffect(() => {
    Promise.all([
      catalogoService.getAñosEscolares(),
      catalogoService.getGrados(),
      catalogoService.getSecciones(),
    ]).then(([años, grados, secciones]) => {
      setAñosEscolares(años);
      setTodosGrados(grados);
      setTodasSecciones(secciones);
      // Pre-seleccionar el año escolar activo
      const activo = años.find(a => a.activo);
      if (activo) setAnioId(activo.idAnioEscolar);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuccess = () => { setVista('list'); if (buscado) loadHorarios(); };

  const handleBuscar = () => {
    if (!anioId || !nivel) return;
    setBuscado(true);
    loadHorarios();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try { await horarioService.eliminar(deleteId); setDeleteId(null); loadHorarios(); }
    catch { /* httpClient lanza el error */ } finally { setDeleting(false); }
  };

  // Filtrado client-side por búsqueda de texto
  const filtrados = horarios.filter(h =>
    search === '' ||
    `${h.docenteNombres ?? ''} ${h.docenteApellidos ?? ''} ${h.cursoNombre}`.toLowerCase().includes(search.toLowerCase())
  );

  // Días que tienen al menos un horario (para las columnas de la grilla)
  const DIAS_GRID: DiaSemana[] = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO'];
  const diasActivos = DIAS_GRID.filter(d => filtrados.some(h => h.dia === d));

  // Agrupar por sección para la vista de grilla
  const porSeccion = useMemo(() => {
    const map = new Map<string, {
      gradoNombre: string; gradoNivel: string; seccionNombre: string;
      items: HorarioResponse[];
    }>();
    filtrados.forEach(h => {
      const key = `${h.gradoNombre}||${h.seccionNombre}`;
      if (!map.has(key)) map.set(key, { gradoNombre: h.gradoNombre, gradoNivel: h.gradoNivel, seccionNombre: h.seccionNombre, items: [] });
      map.get(key)!.items.push(h);
    });
    return Array.from(map.values()).sort((a, b) =>
      `${a.gradoNombre}${a.seccionNombre}`.localeCompare(`${b.gradoNombre}${b.seccionNombre}`)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrados]);

  if (vista === 'new' || vista === 'edit') {
    return (
      <HorarioFormPage
        idHorario={vista === 'edit' ? editId! : undefined}
        onSuccess={handleSuccess}
        onCancel={() => setVista('list')}
      />
    );
  }

  const añoLabel    = añosEscolares.find(a => a.idAnioEscolar === anioId)?.anio ?? '';
  const gradoLabel  = todosGrados.find(g => g.idGrado === gradoId)?.nombre ?? '';
  const secLabel    = todasSecciones.find(s => s.idSeccion === seccionId)?.nombre ?? '';
  const puedoImprimir = buscado && filtrados.length > 0;

  return (
    <div className="space-y-5">
      <style>{PRINT_STYLES}</style>

      {/* Cabecera — oculta en impresión */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Gestión de Horarios</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {buscado
              ? `${horarios.length} horario${horarios.length !== 1 ? 's' : ''} encontrado${horarios.length !== 1 ? 's' : ''}`
              : 'Selecciona año escolar y nivel para buscar'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {puedoImprimir && (
            <button onClick={() => window.print()}
              className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700
                         font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm border border-slate-200">
              <Ico d={D_PRINT} /> Imprimir
            </button>
          )}
          <button onClick={() => setVista('new')}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                       font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm">
            <Ico d={D_PLUS} /> Registrar Horario
          </button>
        </div>
      </div>

      {/* Filtros — ocultos en impresión */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3 print:hidden">

        {/* Fila 1: año, nivel, grado, sección + botón buscar */}
        <div className="flex flex-wrap gap-3 items-end">
          {/* Año escolar (requerido) */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">
              Año escolar <span className="text-red-500">*</span>
            </label>
            <select value={anioId} onChange={e => { setAnioId(e.target.value ? Number(e.target.value) : ''); setNivel(''); setGradoId(''); setSeccionId(''); setBuscado(false); setHorarios([]); }}
              className={`px-3 py-2 rounded-xl border bg-slate-50 text-sm text-slate-700
                          outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]
                          ${!anioId ? 'border-amber-300' : 'border-slate-200'}`}>
              <option value="">— Seleccionar —</option>
              {añosEscolares.map(a => (
                <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}{a.activo ? ' ✓' : ''}</option>
              ))}
            </select>
          </div>

          {/* Nivel (requerido) — requiere año */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">
              Nivel <span className="text-red-500">*</span>
            </label>
            <select value={nivel}
              onChange={e => { setNivel(e.target.value); setGradoId(''); setSeccionId(''); setBuscado(false); setHorarios([]); }}
              disabled={!anioId}
              className={`px-3 py-2 rounded-xl border bg-slate-50 text-sm text-slate-700
                          outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px] disabled:opacity-50
                          ${anioId && !nivel ? 'border-amber-300' : 'border-slate-200'}`}>
              <option value="">— Seleccionar —</option>
              {niveles.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          {/* Grado — requiere nivel */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Grado</label>
            <select value={gradoId}
              onChange={e => { setGradoId(e.target.value ? Number(e.target.value) : ''); setSeccionId(''); }}
              disabled={!nivel}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px] disabled:opacity-50">
              <option value="">Todos</option>
              {gradosFiltrados.map(g => (
                <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>
              ))}
            </select>
          </div>

          {/* Sección — requiere grado */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Sección</label>
            <select value={seccionId}
              onChange={e => setSeccionId(e.target.value ? Number(e.target.value) : '')}
              disabled={!gradoId}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                         text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px] disabled:opacity-50">
              <option value="">Todas</option>
              {todasSecciones.map(s => (
                <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>
              ))}
            </select>
          </div>

          {/* Botón buscar */}
          <button onClick={handleBuscar} disabled={loading || !anioId || !nivel}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                       text-white text-sm font-semibold transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
            <Ico d={D_SEARCH} />
            Buscar
          </button>

          {/* Limpiar */}
          {(nivel || gradoId || seccionId) && (
            <button onClick={() => {
              setNivel(''); setGradoId(''); setSeccionId('');
              setHorarios([]); setBuscado(false);
            }}
              className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold
                         text-slate-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors">
              × Limpiar
            </button>
          )}
        </div>

        {/* Fila 2: búsqueda texto */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Ico d={D_SEARCH} />
          </span>
          <input type="text" placeholder="Buscar por docente o curso..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50
                       text-sm text-slate-800 placeholder-slate-300 outline-none
                       focus:ring-2 focus:ring-blue-500 transition-all" />
        </div>
      </div>

      {/* Área imprimible */}
      <div id="horarios-print-area">

      {/* Encabezado solo visible en impresión */}
      {puedoImprimir && (
        <div className="hidden print:block mb-6">
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-widest mb-1">
            {getConfig().institucionNombre}
          </p>
          <h2 className="text-lg font-bold text-slate-800">Horario Escolar</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Año Escolar: <strong>{añoLabel}</strong>
            {nivel && <> · Nivel: <strong>{nivel}</strong></>}
            {gradoLabel && <> · Grado: <strong>{gradoLabel}</strong></>}
            {secLabel && <> · Sección: <strong>{secLabel}</strong></>}
          </p>
        </div>
      )}

      {/* Contenido */}
      {loading ? (
        <div className="flex justify-center items-center py-16 bg-white rounded-2xl border border-slate-100">
          <SpinnerIcon />
        </div>
      ) : error ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100 text-red-500 text-sm">{error}</div>
      ) : !buscado ? (
        /* Estado inicial — aún no se ha ejecutado ninguna búsqueda */
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.3}
            className="w-14 h-14 text-slate-300 mx-auto mb-4">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
          </svg>
          <p className="text-slate-600 font-semibold">Selecciona año escolar y nivel</p>
          <p className="text-slate-400 text-sm mt-1">
            Elige los filtros requeridos (<span className="text-red-400 font-medium">*</span>) y presiona <strong>Buscar</strong> para ver los horarios
          </p>
        </div>
      ) : filtrados.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
            className="w-12 h-12 text-slate-200 mx-auto mb-3">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
          </svg>
          <p className="text-slate-400 text-sm font-medium">No se encontraron horarios</p>
          <p className="text-slate-300 text-xs mt-1">Cambia los filtros o registra uno nuevo</p>
        </div>
      ) : (

        /* ── Vista grilla por sección ── */
        <div className="space-y-6">
          {porSeccion.map(({ gradoNombre, gradoNivel, seccionNombre, items }) => {
            // Franjas únicas ordenadas
            const franjas = Array.from(
              new Set(items.map(h => `${h.horaInicio}|${h.horaFin}`))
            )
              .sort()
              .map(f => { const [ini, fin] = f.split('|'); return { horaInicio: ini, horaFin: fin }; });

            const getCelda = (dia: DiaSemana, ini: string, fin: string) =>
              items.filter(h => h.dia === dia && h.horaInicio === ini && h.horaFin === fin);

            return (
              <div key={`${gradoNombre}-${seccionNombre}`}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden horarios-print-section">

                {/* Cabecera de sección */}
                <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50 horarios-section-header">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full horarios-nivel-badge
                    ${NIVEL_COLOR[gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                    {gradoNivel}
                  </span>
                  <h3 className="font-bold text-slate-800 text-sm">
                    {gradoNombre} — Sección {seccionNombre}
                  </h3>
                  <span className="ml-auto text-xs text-slate-400 print:hidden">{items.length} registro{items.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Grilla */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-700 text-white horarios-thead">
                        <th className="px-3 py-2 text-left font-semibold w-28 border-r border-slate-600 whitespace-nowrap">
                          Hora
                        </th>
                        {diasActivos.map(d => (
                          <th key={d} className="px-3 py-2 text-center font-semibold border-r border-slate-600 last:border-r-0">
                            {DIA_LABEL[d]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="horarios-tbody">
                      {franjas.map((franja, fi) => (
                        <tr key={fi} className={`border-b border-slate-200 ${fi % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                          <td className="px-3 py-2 text-slate-500 font-medium border-r border-slate-200 whitespace-nowrap align-middle">
                            {fmt(franja.horaInicio)} – {fmt(franja.horaFin)}
                          </td>
                          {diasActivos.map(d => {
                            const celda = getCelda(d, franja.horaInicio, franja.horaFin);
                            return (
                              <td key={d} className="px-2 py-1.5 border-r border-slate-200 last:border-r-0 align-top">
                                {celda.map(h => {
                                  const esRecreo = h.tipo === 'RECREO';
                                  return (
                                    <div key={h.idHorario}
                                      className={`mb-1 last:mb-0 rounded-lg p-1.5 group relative
                                        ${esRecreo ? 'bg-green-50 horarios-cell-recreo' : 'bg-blue-50 hover:bg-blue-100 horarios-cell-clase'} transition-colors`}>
                                      {esRecreo ? (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-700">
                                          <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                                            <path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm.75-10.25a.75.75 0 0 0-1.5 0v3.5c0 .414.336.75.75.75h3a.75.75 0 0 0 0-1.5H8.75v-2.75Z" clipRule="evenodd"/>
                                          </svg>
                                          RECREO
                                        </span>
                                      ) : (
                                        <>
                                          <p className="font-semibold text-slate-800 leading-tight truncate">{h.cursoNombre}</p>
                                          <p className="text-slate-500 leading-tight truncate">
                                            {h.docenteNombres} {h.docenteApellidos}
                                          </p>
                                        </>
                                      )}
                                      {/* Botones de acción — ocultos en impresión */}
                                      <div className="flex gap-0.5 mt-1 print:hidden">
                                        <ActionBtn title="Editar" color="blue"
                                          onClick={() => { setEditId(h.idHorario); setVista('edit'); }}>
                                          <Ico d={D_EDIT} cls="w-3 h-3" />
                                        </ActionBtn>
                                        <ActionBtn title="Eliminar" color="red"
                                          onClick={() => setDeleteId(h.idHorario)}>
                                          <Ico d={D_DEL} cls="w-3 h-3" />
                                        </ActionBtn>
                                      </div>
                                    </div>
                                  );
                                })}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      </div>{/* fin #horarios-print-area */}

      {/* Modal eliminación — oculto en impresión */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                  <Ico d={D_DEL} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Eliminar Horario</h3>
                  {(() => {
                    const h = horarios.find(x => x.idHorario === deleteId);
                    return h ? (
                      <p className="text-slate-400 text-xs">
                        {DIA_LABEL[h.dia]} · {fmt(h.horaInicio)}–{fmt(h.horaFin)} · {h.cursoNombre}
                      </p>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-slate-600 text-sm">¿Estás seguro? Esta acción no se puede deshacer.</p>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setDeleteId(null)} disabled={deleting}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold
                           text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDelete} disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-sm font-bold
                           text-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {deleting && <SpinnerIcon />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HorariosPage;
